import { collectStateIds, type StateMachine } from "@pixelpuf/schemas/pxpf";
import { bfs } from "@statelyai/graph";
import { actionCatalogIds } from "./catalogs/actions.ts";
import { guardCatalogIds } from "./catalogs/guards.ts";
import { type NodeData, toVisualGraph } from "./flow.ts";

/**
 * Pure validation pass over a `StateMachine`. Surfaces issues for the editor
 * to render as warnings / errors. Distinct from `buildUserMachine` (which
 * throws on hard issues) — this returns a structured report and never throws,
 * so the editor can render partial-invalid states (a half-drawn machine).
 */

interface ValidationIssue {
  severity: "error" | "warning";
  code: string;
  message: string;
  /** Affected state id, transition index, or undefined for machine-level. */
  ref?: { kind: "state"; id: string } | { kind: "transition"; index: number } | { kind: "machine" };
}

interface ValidationReport {
  issues: ValidationIssue[];
  hasErrors: boolean;
}

const EMPTY_LAYOUT = {} as const;

export function validateMachine(machine: StateMachine): ValidationReport {
  const issues: ValidationIssue[] = [];
  const stateIds = collectStateIds(machine);

  // ─── machine-level: initial must reference a state declared in states[] or
  // transitions[]. We can't ask `stateIds.has(initial)` because collectStateIds
  // adds initial itself, so the check would self-satisfy. ───
  const initialIsDeclared = (machine.states?.some((s) => s.id === machine.initial) ?? false)
    || machine.transitions.some((t) => t.from === machine.initial || t.to === machine.initial);
  if (machine.initial === "" || !initialIsDeclared) {
    issues.push({
      severity: "error",
      code: "missing_initial",
      message: machine.initial === ""
        ? "machine has no initial state"
        : `initial state "${machine.initial}" is not declared`,
      ref: { kind: "machine" },
    });
  }

  // ─── transitions: catalog-id misses, event collisions. (Dangling-target
  // checks omitted: the act of declaring a transition with target "ghost"
  // brings "ghost" into stateIds, so by definition it can't be dangling under
  // our schema.) ───
  const eventByFrom = new Map<string, Map<string, number[]>>();
  machine.transitions.forEach((t, i) => {
    if (t.guard && !guardCatalogIds.has(t.guard.id)) {
      issues.push({
        severity: "error",
        code: "unknown_guard",
        message: `unknown guard id "${t.guard.id}"`,
        ref: { kind: "transition", index: i },
      });
    }
    if (t.actions) {
      for (const a of t.actions) {
        if (!actionCatalogIds.has(a.id)) {
          issues.push({
            severity: "error",
            code: "unknown_action",
            message: `unknown action id "${a.id}"`,
            ref: { kind: "transition", index: i },
          });
        }
      }
    }

    // Collision tracking: same (from, event) with no distinguishing guard.
    const eventMap = eventByFrom.get(t.from) ?? new Map<string, number[]>();
    const indices = eventMap.get(t.event) ?? [];
    indices.push(i);
    eventMap.set(t.event, indices);
    eventByFrom.set(t.from, eventMap);
  });

  for (const [from, eventMap] of eventByFrom) {
    for (const [event, indices] of eventMap) {
      if (indices.length < 2) continue;
      // Only warn when at least one of the colliders has no guard — those would
      // unconditionally take the same path. Multiple guarded variants are fine.
      const hasUnguarded = indices.some((i) => !machine.transitions[i]?.guard);
      if (!hasUnguarded) continue;
      const firstIndex = indices[0];
      if (firstIndex === undefined) continue;
      issues.push({
        severity: "warning",
        code: "transition_collision",
        message: `state "${from}" has multiple unguarded transitions on event "${event}"`,
        ref: { kind: "transition", index: firstIndex },
      });
    }
  }

  // ─── per-state: entry/exit catalog-id misses ───
  if (machine.states) {
    for (const s of machine.states) {
      if (s.entry) {
        for (const a of s.entry) {
          if (!actionCatalogIds.has(a.id)) {
            issues.push({
              severity: "error",
              code: "unknown_entry_action",
              message: `state "${s.id}" entry references unknown action "${a.id}"`,
              ref: { kind: "state", id: s.id },
            });
          }
        }
      }
      if (s.exit) {
        for (const a of s.exit) {
          if (!actionCatalogIds.has(a.id)) {
            issues.push({
              severity: "error",
              code: "unknown_exit_action",
              message: `state "${s.id}" exit references unknown action "${a.id}"`,
              ref: { kind: "state", id: s.id },
            });
          }
        }
      }
    }
  }

  // ─── reachability: warn about states unreachable from initial ───
  // Use @statelyai/graph BFS over a layout-free VisualGraph (positions don't
  // affect reachability — empty layout is fine). Skip when there's no usable
  // initial — the missing_initial error already surfaces that gap.
  if (machine.initial !== "" && stateIds.has(machine.initial) && stateIds.size > 1) {
    const graph = toVisualGraph(machine, EMPTY_LAYOUT);
    const reachable = new Set<string>();
    for (const node of bfs<NodeData>(graph, machine.initial)) {
      reachable.add(node.id);
    }
    for (const id of stateIds) {
      if (!reachable.has(id)) {
        issues.push({
          severity: "warning",
          code: "unreachable",
          message: `state "${id}" is unreachable from initial "${machine.initial}"`,
          ref: { kind: "state", id },
        });
      }
    }
  }

  return {
    issues,
    hasErrors: issues.some((i) => i.severity === "error"),
  };
}
